import React, { useState } from 'react';
import { Header } from './components/Header';
import { SchematicDiagram } from './components/SchematicDiagram';
import { ControlToolbar } from './components/ControlToolbar';
import { Footer } from './components/Footer';
import { ThemeProvider } from './context/ThemeContext';
import { Modal } from './components/Modal';
import { SettingsPanel } from './components/SettingsPanel';
import { ResultsModal } from './components/ResultsModal';
export function App() {
  const [isConnected, setIsConnected] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showSettingsPanel, setShowSettingsPanel] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [modalContent, setModalContent] = useState({
    title: '',
    message: '',
    confirmAction: () => {}
  });
  const [logs, setLogs] = useState([{
    time: '10:30:22',
    message: 'System initialized',
    status: 'success' as const
  }, {
    time: '10:30:25',
    message: 'Chambers ready',
    status: 'success' as const
  }]);
  const [results, setResults] = useState([{
    pathogen: 'E. coli',
    type: 'Bacteria',
    detected: true,
    concentration: '105'
  }, {
    pathogen: 'S. aureus',
    type: 'Bacteria',
    detected: false,
    concentration: '0'
  }]);
  // Bottle states
  const [bottles, setBottles] = useState({
    pbs: {
      level: 80,
      filling: false
    },
    pathogen: {
      level: 60,
      filling: false
    },
    waste: {
      level: 30,
      filling: false
    }
  });
  // Chamber states - now includes liquidColor
  const [chambers, setChambers] = useState([{
    id: 1,
    filled: false,
    detecting: false,
    spceActive: false,
    liquidColor: '#007BFF'
  }, {
    id: 2,
    filled: false,
    detecting: false,
    spceActive: false,
    liquidColor: '#007BFF'
  }, {
    id: 3,
    filled: false,
    detecting: false,
    spceActive: false,
    liquidColor: '#007BFF'
  }, {
    id: 4,
    filled: false,
    detecting: false,
    spceActive: false,
    liquidColor: '#007BFF'
  }]);
  // Animation states for fluid flows
  const [flows, setFlows] = useState({
    pbsToChamber: false,
    pathogenToChamber: false,
    chamberToWaste: false
  });
  const addLog = (message: string, status = 'info') => {
    const now = new Date();
    const time = `${now.getHours()}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;
    setLogs(prev => [...prev, {
      time,
      message,
      status: status as 'success' | 'error' | 'warning' | 'info'
    }]);
  };
  const fillPBS = () => {
    setBottles(prev => ({
      ...prev,
      pbs: {
        ...prev.pbs,
        filling: true
      }
    }));
    setFlows(prev => ({
      ...prev,
      pbsToChamber: true
    }));
    addLog('Filling chambers with PBS solution', 'info');
    // Simulate filling process
    setTimeout(() => {
      setChambers(prev => prev.map(chamber => ({
        ...chamber,
        filled: true,
        liquidColor: '#007BFF'
      })));
      setBottles(prev => ({
        ...prev,
        pbs: {
          level: prev.pbs.level - 10,
          filling: false
        }
      }));
      setFlows(prev => ({
        ...prev,
        pbsToChamber: false
      }));
      addLog('Chambers filled with PBS solution', 'success');
    }, 3000);
  };
  const fillPathogen = () => {
    // Show confirmation modal first
    setModalContent({
      title: 'Confirm Action',
      message: 'Are you sure you want to fill chambers with pathogen solution?',
      confirmAction: () => {
        setShowModal(false);
        performFillPathogen();
      }
    });
    setShowModal(true);
  };
  const performFillPathogen = () => {
    setBottles(prev => ({
      ...prev,
      pathogen: {
        ...prev.pathogen,
        filling: true
      }
    }));
    setFlows(prev => ({
      ...prev,
      pathogenToChamber: true
    }));
    addLog('Filling chambers with pathogen solution', 'warning');
    // Simulate filling process
    setTimeout(() => {
      setChambers(prev => prev.map(chamber => ({
        ...chamber,
        filled: true,
        liquidColor: '#FFA500'
      })));
      setBottles(prev => ({
        ...prev,
        pathogen: {
          level: prev.pathogen.level - 10,
          filling: false
        }
      }));
      setFlows(prev => ({
        ...prev,
        pathogenToChamber: false
      }));
      addLog('Chambers filled with pathogen solution', 'success');
    }, 3000);
  };
  const emptyToWaste = () => {
    setFlows(prev => ({
      ...prev,
      chamberToWaste: true
    }));
    addLog('Emptying chambers to waste', 'info');
    // Simulate emptying process
    setTimeout(() => {
      setChambers(prev => prev.map(chamber => ({
        ...chamber,
        filled: false
      })));
      setBottles(prev => ({
        ...prev,
        waste: {
          level: prev.waste.level + 10,
          filling: true
        }
      }));
      setTimeout(() => {
        setBottles(prev => ({
          ...prev,
          waste: {
            ...prev.waste,
            filling: false
          }
        }));
        setFlows(prev => ({
          ...prev,
          chamberToWaste: false
        }));
        addLog('Chambers emptied to waste', 'success');
      }, 1000);
    }, 3000);
  };
  const performDetection = () => {
    addLog('Starting pathogen detection process', 'info');
    setChambers(prev => prev.map(chamber => ({
      ...chamber,
      detecting: true
    })));
    // Simulate detection process
    setTimeout(() => {
      setChambers(prev => prev.map(chamber => ({
        ...chamber,
        spceActive: true
      })));
      setTimeout(() => {
        setChambers(prev => prev.map(chamber => ({
          ...chamber,
          detecting: false,
          spceActive: false
        })));
        addLog('Detection complete', 'success');
        // Add sample results
        setResults([{
          pathogen: 'E. coli',
          type: 'Bacteria',
          detected: true,
          concentration: '105'
        }, {
          pathogen: 'S. aureus',
          type: 'Bacteria',
          detected: true,
          concentration: '87'
        }, {
          pathogen: 'Influenza A',
          type: 'Virus',
          detected: true,
          concentration: '92'
        }, {
          pathogen: 'Candida albicans',
          type: 'Fungi',
          detected: true,
          concentration: '45'
        }, {
          pathogen: 'Aspergillus',
          type: 'Fungi',
          detected: false,
          concentration: '0'
        }, {
          pathogen: 'Pollen',
          type: 'Allergen',
          detected: true,
          concentration: '68'
        }]);
        // Show results modal
        setShowResultsModal(true);
      }, 5000);
    }, 2000);
  };
  const toggleConnection = () => {
    setIsConnected(!isConnected);
    addLog(isConnected ? 'Disconnected from instrument' : 'Connected to instrument', isConnected ? 'error' : 'success');
  };
  const handleSaveResults = () => {
    addLog('Results saved successfully', 'success');
    setShowResultsModal(false);
  };
  return <ThemeProvider data-id="element-0">
      <div className="flex flex-col h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 overflow-hidden" data-id="element-1">
        <Header isConnected={isConnected} onConnectionToggle={toggleConnection} onOpenSettings={() => setShowSettingsPanel(true)} data-id="element-2" />
        <div className="flex flex-1 p-2 overflow-hidden" data-id="element-3">
          <div className="w-full flex flex-col" data-id="element-4">
            <SchematicDiagram bottles={bottles} chambers={chambers} flows={flows} data-id="element-5" />
            <ControlToolbar onFillPBS={fillPBS} onFillPathogen={fillPathogen} onEmptyToWaste={emptyToWaste} onPerformDetection={performDetection} chambersReady={chambers.some(c => c.filled)} data-id="element-6" />
          </div>
        </div>
        <Footer data-id="element-7" />
        {showModal && <Modal title={modalContent.title} message={modalContent.message} onConfirm={modalContent.confirmAction} onCancel={() => setShowModal(false)} data-id="element-8" />}
        {showSettingsPanel && <SettingsPanel logs={logs} onClose={() => setShowSettingsPanel(false)} data-id="element-9" />}
        {showResultsModal && <ResultsModal results={results} onSave={handleSaveResults} onClose={() => setShowResultsModal(false)} data-id="element-10" />}
      </div>
    </ThemeProvider>;
}